// node modules
import * as React from "react";
import { Layout, Modal, Skeleton, Input, Button } from "antd";
import { useHistory } from 'react-router-dom';
import { sha256 } from "js-sha256";
// components
import HeaderComponent from "components/general/HeaderComponent";
import SidebarComponent from "components/general/SidebarComponent";
import ConfirmationNotification from "components/general/ConfirmationNotification";
// hocs
import RequiredAuth from "hocs/RequiredAuth";
// apis
import userApis from "apis/userApis";
import tokenApis from "apis/tokenApis";
import organizationApis from "apis/organizationApis";
import deviceApis from "apis/deviceApis";
// config
import { getFcmToken, onMessage } from "../../firebase";

// Notification
import { successNotif, info } from "utils/notification";

import 'styles/dashboardLayout.css';

import emitter from "utils/emitter";
import tokenService from "utils/tokenService";
import { useTranslation } from "react-i18next";

let backgroundNotificationsChannel;
try {
	backgroundNotificationsChannel = new BroadcastChannel('background-notifications');
} catch (error) {

}

const { Content, Footer } = Layout;
export const UserDetailContext = React.createContext(null);

const DashboardLayout = ({ children }) => {
	const { t } = useTranslation()
	// If there is a pending organization invitation
	const joinOrganization = localStorage.getItem('joinOrganization') === 'true';
	const invitedOrganizationName = localStorage.getItem('organizationName');

	// If there is a pending accept shared device
	const sharedDeviceActivationToken = localStorage.getItem('sharedDeviceActivationToken');

	// ******************************************************* States ***************************************************
	const [userInfo, setUserInfo] = React.useState({});
	const [notifications, setNotifications] = React.useState(null);
	const [isTokenFound, setTokenFound] = React.useState(true);

	// Used to set the organization invitation modal button to loading 
	const [joinOrgModalLoading, setJoinOrgModalLoading] = React.useState(false);

  // Used to close and open the modal
  const [joinOrgModalOpen, setJoinOrgModalOpen] = React.useState(joinOrganization);

	// Accept shared device button loading state
	const [acceptSharedDeviceLoading, setAcceptSharedDeviceLoading] = React.useState(false);

	const [acceptSharedDeviceFetching, setAcceptSharedDeviceFetching] = React.useState(false);

	// Whether the accept shared device modal should be open or not
	const [acceptSharedDeviceModalOpen, setAcceptSharedDeviceModalOpen] = React.useState(false);

	// The answer to the security question that user has entered
	const [acceptSharedDeviceSecurityAnswer, setAcceptSharedDeviceSecurityAnswer] = React.useState('');

	// The security question and shared device id that is fetched from the server
	const [sharedDeviceQuestion, setSharedDeviceQuestion] = React.useState(null);
	const [sharedDeviceId, setSharedDeviceId] = React.useState(null);

	// A ref to the accept shared device submit button is modal
	const acceptsharedDeviceSubmitRef = React.useRef(null);

	const history = useHistory();

	// ************************************************* Functions ******************************************************
	/**
	 * Get the notification data object and generate a message to put in the notification center in the header component
	 * @param {object} data - The notification data object
	 * @returns {string} The message to show
	 */
	const generateNotificationMessage = data => {
		if (data.type === 'update-user') {
			const fields = { name: 'NAME', phoneNumber: 'PHONE_NUMBER', picture: 'profilePicture', email: 'EMAIL' };
			let field = '';
			for (const f of Object.keys(fields)) {
				if (data[f])
					field = fields[f];
			}
			return t('notifications.update-user', { fieldName: t(field) });
		}

		// Read notification description from i18n locales
		const notificationDesc = t(`notifications.${data.type}`, data);

		// If notification description is not defined in i18n locales, then the value will be data.type
		return notificationDesc === data.type ? '' : notificationDesc;
	};

	const setFcmToken = async () => {
		const fcmToken = await getFcmToken();
		if (fcmToken) {
			setTokenFound(true);
			try {
				await tokenApis.postFcmToken({ payload: { fcmToken } });
			} catch (error) {
				console.log("something wrong in fcm token saving");
				setTokenFound(false);
			}
		} else {
			setTokenFound(false);
		}
	};

	const enableConfirmationBox = React.useCallback(() => {
		// Let's check if the browser supports notifications
		if (!("Notification" in window)) {
			setTokenFound(true);
			alert("This browser does not support desktop notification");
		}

		// Let's check whether notification permissions have already been granted
		else if (Notification.permission === "granted") {
			// If it's okay let's create a notification
			setTokenFound(true);
		}

		// Otherwise, we need to ask the user for permission
		else {
			Notification.requestPermission().then(function (permission) {
				// If the user accepts, let's create a notification
				if (permission === "granted" || permission === "default") {
					setFcmToken();
				} else {
					alert("Notifications blocked. Please enable them in your browser.");
					setTokenFound(true);
				}
			});
		}
		// At last, if the user has denied notifications, and you
		// want to be respectful there is no need to bother them any more.
	}, []);

	// ************************************************* Use Effect *****************************************************
	// Listen on push notifications and emit events so handlers in the entire application can attach a listener to it
	React.useEffect(() => {
		// Foreground notfications
		onMessage(payload => {
			if (!payload) return;
			emitter.emit(payload.data.type, payload.data);
			setNotifications(generateNotificationMessage(payload.data))
		});

		// Background notifications
		backgroundNotificationsChannel && backgroundNotificationsChannel.addEventListener('message', event => {
			emitter.emit(event.data.data.type, event.data.data);
			setNotifications(generateNotificationMessage(event.data.data))
		});
	}, []);

	React.useEffect(() => {
		userApis
			.getUserInfo({})
			.then((response) => {
				setUserInfo(response);
				setFcmToken();
			})
			.catch((error) => {
			});
	}, []);

	// Listen on events
	React.useEffect(() => {
		// Store listeners and evvents in an object so we can remove them from emitter
		const listeners = {
			'USER_UPDATED' (updatedUserObject) {
				setUserInfo({ ...updatedUserObject });
			},
			'update-user' (data) {
				const updatedFields = {};
				for (const field of ['name', 'email', 'picture', 'phoneNumber']) {
					if (data[field]) updatedFields[field] = data[field]
				}
				setUserInfo({ ...structuredClone(userInfo), ...updatedFields });
			},
			'delete-avatar' (_) {
				setUserInfo({ ...structuredClone(userInfo), picture: '' });
			},
			'delete-account' (_) {
				tokenService.clearToken();
				info(t('accountDeleted'));
				history.replace('/login');
			},
			'logout' (_) {
				info(t('loggedOut'));
				tokenService.clearToken();
				history.replace('/login');
			}
		};

		emitter.on('USER_UPDATED', listeners.USER_UPDATED);
		emitter.on('update-user', listeners['update-user']);
		emitter.on('delete-avatar', listeners['delete-avatar']);
		emitter.on('delete-account', listeners['delete-account']);
		emitter.on('logout', listeners['logout']);

		// Remove attached events on user update
		return () => {
			for (const [event, listener] of Object.entries(listeners)) {
				emitter.removeListener(event, listener);
			}
		}
	}, [userInfo]);

	// Try to fetch the shared device if user has clicked on the accept button in their email
	React.useEffect(() => {
		if (!sharedDeviceActivationToken) return;

		setAcceptSharedDeviceModalOpen(true);
		setAcceptSharedDeviceFetching(true);
		deviceApis
			.shareDeviceActiveEmail({ params: { token: sharedDeviceActivationToken } })
			.then(result => {
				setSharedDeviceId(result._id);
				setSharedDeviceQuestion(result.question);
			}).catch(err => {
				setAcceptSharedDeviceModalOpen(false);
				localStorage.removeItem('sharedDeviceActivationToken');
			}).finally(() => {
				setAcceptSharedDeviceFetching(false);
			});
	}, []);

	return (
		<UserDetailContext.Provider value={{ notifications, userInfo }}>
			<Layout
				hasSider
				className="site-layout-background"
				data-testid="dashboard-layout"
			>
				<SidebarComponent userInfo={userInfo} />
				<Layout
					style={{
						overflow: 'hidden'
					}}
				>
					{!isTokenFound && (
						<ConfirmationNotification
							enableConfirmationBox={enableConfirmationBox}
						/>
					)}
					{
						joinOrganization &&
						<Modal
							title={t("joinOrganization", { invitedOrganizationName })}
							visible={joinOrgModalOpen}
							onOk={() => {
								setJoinOrgModalLoading(true);
								organizationApis.joinOrganization({
									payload: {
										invitationToken: localStorage.getItem('invitationToken')
									},
									options: {
										errorMessages: {
											ORG_016: t('errors.custom.ADMIN_CANT_JOIN_AN_ORG')
										}
									}
								})
									.then(({ message }) => {
										successNotif(t("joinedOrganization", { invitedOrganizationName }));
									})
									.catch(error => {
									}).finally(() => {
										setJoinOrgModalLoading(false);
										setJoinOrgModalOpen(false);
										localStorage.removeItem('invitationToken');
										localStorage.removeItem('joinOrganization');
										localStorage.removeItem('organizationName');
									});
							}}
							okText={t('YES')}
							confirmLoading={joinOrgModalLoading}
							onCancel={() => {
								localStorage.removeItem('invitationToken');
								localStorage.removeItem('joinOrganization');
								localStorage.removeItem('organizationName');
								setJoinOrgModalOpen(false);
								setJoinOrgModalLoading(false);
							}}
							maskClosable={false}
							cancelText={t('NO')}
							closable={false}
						>
							<p>{t("organizationInvite", { invitedOrganizationName })}</p>
						</Modal>
					}
					{
						sharedDeviceActivationToken &&
						<Modal
							className="accept-shared-device-modal"
							title={t('ADD_SHARED_DRIVE')}
							visible={acceptSharedDeviceModalOpen}
							maskClosable={false}
							closable={false}
							footer={
								<>
									<Button
										className="accept-shared-device-btn-cancel"
										onClick={() => {
											setAcceptSharedDeviceModalOpen(false);
											setTimeout(() => localStorage.removeItem('sharedDeviceActivationToken'), 200)
										}}
									>
										{t('CANCEL')}
									</Button>
									<Button
										ref={acceptsharedDeviceSubmitRef}
										loading={acceptSharedDeviceLoading}
										disabled={acceptSharedDeviceLoading || acceptSharedDeviceFetching}
										className="accept-shared-device-btn"
										style={{ marginLeft: '5px' }}
										onClick={() => {
											setAcceptSharedDeviceLoading(true);
											deviceApis.shareDeviceVerify({
												payload: {
													id: sharedDeviceId,
													answer: sha256(acceptSharedDeviceSecurityAnswer).toUpperCase()
												}
											})
												.then(result => {
													const resultName = result.name
													successNotif(t("sharedWithYou", { resultName }));
													setTimeout(() => localStorage.removeItem('sharedDeviceActivationToken'), 200)
													setAcceptSharedDeviceModalOpen(false);
												})
												.catch(error => {
													setAcceptSharedDeviceLoading(false);
												});
										}}
									>
										{t('continue')}
									</Button>
								</>
							}
						>
							{
								acceptSharedDeviceFetching &&
								<>
									<Skeleton.Input active={true} size="small" style={{ width: '60%', marginTop: '0.3rem' }} />
									<Skeleton.Input active={true} size="small" style={{ width: '100%', margin: '1.3rem 0 1.5rem 0' }} />
								</>
							}
							{
								!acceptSharedDeviceFetching &&
								<>
									<h3>{sharedDeviceQuestion}</h3>
									<Input
										onPressEnter={() => acceptsharedDeviceSubmitRef.current.click()}
										onChange={(e) => {
											setAcceptSharedDeviceSecurityAnswer(e.target.value);
										}}
										autoFocus={true}
										style={{ margin: '0.7rem 0 1.2rem 0' }}
										placeholder={t('ENTER_SECURITY_ANSWER')}
									/>
								</>
							}
						</Modal>
					}
					<HeaderComponent userInfo={userInfo} notifications={notifications} />
					<Content
						className="site-layout-background"
						style={{
							padding: "10px 0 10px 0",
							margin: 0,
							overflowX: 'hidden',
							overflowY: 'auto',
							paddingBottom: '100px'
						}}
					>
						{userInfo._id && children}
					</Content>
					{/*<Footer className="text-align-center">*/}
					{/*  {isTokenFound && <h1> Notification permission enabled 👍🏻 </h1>}*/}
					{/*  {!isTokenFound && <h1> Need notification permission ❗️ </h1>}*/}
					{/*</Footer>*/}
				</Layout>
			</Layout>
		</UserDetailContext.Provider>
	);
};

export default RequiredAuth(DashboardLayout);
